import type { Suscripcion } from '../../api/operaciones';
import type { PaqueteWhatsApp, ProductoCatalogo } from '../../api/ops/plan';
import type { NombreIcono } from '../../componentes/iconos';
import type { EstadoChip } from '../../componentes/Chip';
import { dinero, miles, NOMBRE_PAQUETE } from './logica';

/**
 * Recarga automática de los paquetes de WhatsApp en Consumo (1.16; T.1 del 05-nov, G1237, G1239, G1241, G1242, G1244):
 * textos y cuentas sin estilos. El estado sale de `WhatsAppPack.auto_top_up` (`listWhatsAppPacks`) y se cambia con
 * `setWhatsAppPackAutoTopUp` (solo el propietario).
 *
 * - **Apagada tras un cobro fallido** (G1239, G1407, G1408): con `last_failed_at`, «Desactivada» y el aviso rojo «No se ha
 *   podido recargar hoy, a las 07:14» con «Volver a activar», de un clic con el tamaño y el umbral que se conservaron.
 *
 * - **Precio de cada tamaño** («se compran 1.000 mensajes por 31 €», «Cada recarga: 31 €»): del catálogo público
 *   (`getCatalog`, G1415); la tarjeta guardada («con Visa •••• 4242»), de `Subscription.payment_method`. Sin catálogo o sin
 *   precio, se dice solo el tamaño; sin tarjeta, «con la tarjeta guardada».
 * - Activar, cambiar o quitar la recarga no espera a `cobroDisponible()` (G1416, completa G92): la ve abierta el
 *   propietario con plan contratado.
 */

type Paquete = PaqueteWhatsApp['pack'];

export const PAQUETES_RECARGA: readonly { pack: Paquete; icono: NombreIcono }[] = [
    { pack: 'transactional', icono: 'message-circle' },
    { pack: 'chatbot', icono: 'bot' },
    { pack: 'marketing', icono: 'megaphone' },
];

/** Umbrales del desplegable «Cuando queden menos de» (dibujo 1.16). */
export const UMBRALES = [100, 200, 500, 1000, 2000] as const;

/** Tamaños que se pueden comprar (`paquete_<servicio|chatbot|marketing>_<1000|2000|5000>`, `RecargaAutomatica` del back). */
export const TAMANOS = [1000, 2000, 5000] as const;

const PARTE: Readonly<Record<Paquete, string>> = { transactional: 'servicio', chatbot: 'chatbot', marketing: 'marketing' };

export const codigoDeTamano = (pack: Paquete, tamano: number): string => `paquete_${PARTE[pack]}_${String(tamano)}`;

/** El tamaño de un `product_code` («paquete_servicio_2000» → 2000); si no se entiende, null. */
export function tamanoDe(codigo: string | null | undefined): number | null {
    const m = /_(\d+)$/.exec(codigo ?? '');
    return m === null ? null : Number(m[1]);
}

export const AYUDA_RECARGA =
    'Cuando el saldo de un paquete baja del umbral, se compra el tamaño que elijas con la tarjeta guardada. Si después de una recarga el saldo sigue por debajo del umbral, no se vuelve a cobrar hasta que lo supere.';

export const SOLO_PROPIETARIO = 'Solo el propietario activa o cambia la recarga automática.';

/**
 * ¿Tiene plan contratado (G1416)? Fuera de la prueba, de la prueba caducada y de la baja o el cierre. El back exige además
 * la suscripción de Stripe con su método de pago (409 si falta).
 */
export const contratado = (s: Suscripcion): boolean => !['trialing', 'trial_expired', 'cancelled', 'closed'].includes(s.status);

/** Precio de un tamaño («31 €»), si se conoce. */
export type PrecioDe = (codigo: string) => string | null;

/** El precio vigente de cada producto del catálogo (`getCatalog`, G1415) por su código; sin catálogo, ninguno. */
export function precioDelCatalogo(productos: readonly ProductoCatalogo[] | null): PrecioDe {
    const precios = new Map((productos ?? []).flatMap((p) => (p.price == null ? [] : [[p.code, dinero(p.price.unit)] as const])));
    return (codigo) => precios.get(codigo) ?? null;
}

const DIAS_FALLO = (d: Date, ahora: Date): string => {
    const dia = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const dias = Math.round((dia(ahora) - dia(d)) / 86_400_000);
    return dias === 0 ? 'hoy' : dias === 1 ? 'ayer' : `el ${d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}`;
};

/** Cuándo se apagó la recarga por un cobro fallido (`last_failed_at`, G1407): «hoy» y «07:14»; si no falló, null. */
export function falloDe(paquete: PaqueteWhatsApp, ahora = new Date()): { dia: string; hora: string } | null {
    const cuando = paquete.auto_top_up.last_failed_at;
    if (paquete.auto_top_up.enabled || cuando == null) {
        return null;
    }
    const d = new Date(cuando);
    return { dia: DIAS_FALLO(d, ahora), hora: d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) };
}

const mensajes = (n: number) => `${miles(n)} mensajes`;

export interface FilaRecarga {
    pack: Paquete;
    nombre: string;
    icono: NombreIcono;
    activa: boolean;
    chip: { texto: string; estado: EstadoChip };
    linea: string;
    /** Cuándo fue el cobro fallido, si se apagó por uno (aviso rojo). */
    fallo: { dia: string; hora: string } | null;
}

export function filaRecarga(p: PaqueteWhatsApp, icono: NombreIcono, precioDe: PrecioDe, ahora = new Date()): FilaRecarga {
    const a = p.auto_top_up;
    const quedan = miles(p.messages_available);
    const fallo = falloDe(p, ahora);
    const base = { pack: p.pack, nombre: NOMBRE_PAQUETE[p.pack], icono, activa: a.enabled, fallo };
    if (a.enabled) {
        const tamano = tamanoDe(a.product_code);
        const precio = a.product_code == null ? null : precioDe(a.product_code);
        const compra = tamano === null ? 'se compra el tamaño elegido' : `se compran ${mensajes(tamano)}${precio === null ? '' : ` por ${precio}`}`;
        const umbral = a.below_messages == null ? '' : `Cuando queden menos de ${mensajes(a.below_messages)}, `;
        const frase = umbral === '' ? `${compra.charAt(0).toUpperCase()}${compra.slice(1)}` : `${umbral}${compra}`;
        return { ...base, chip: { texto: 'Activa', estado: 'confirmed' }, linea: `${frase}. Ahora quedan ${quedan}.` };
    }
    if (fallo !== null) {
        return { ...base, chip: { texto: 'Desactivada', estado: 'problem' }, linea: `Se apagó tras el cobro fallido de ${fallo.dia}. Ahora quedan ${quedan}.` };
    }
    return { ...base, chip: { texto: 'Apagada', estado: 'neutral' }, linea: `Ahora quedan ${quedan}. Cuando se acaben, se compra a mano en «Comprar más».` };
}

/** El aviso rojo junto al paquete apagado tras un cobro fallido (G1239), con el mismo texto del aviso de la campana. */
export const avisoFallo = (f: { dia: string; hora: string }) => ({
    titulo: `No se ha podido recargar ${f.dia}, a las ${f.hora}`,
    texto: 'El cobro con la tarjeta guardada no ha salido y hemos desactivado la recarga automática de este paquete. Revisa el método de pago y vuelve a activarla, o compra el paquete a mano para que no se corten los mensajes.',
});

/** Textos de la ventana «Recarga automática · {paquete}» para el umbral y el tamaño elegidos. */
export function textosVentana(p: PaqueteWhatsApp, umbral: number, tamano: number, precioDe: PrecioDe, tarjeta: string | null) {
    const nombre = NOMBRE_PAQUETE[p.pack];
    const precio = precioDe(codigoDeTamano(p.pack, tamano));
    return {
        titulo: `Recarga automática · ${nombre}`,
        texto: `Cuando queden menos mensajes de los que elijas, se compra el tamaño elegido con ${tarjeta ?? 'la tarjeta guardada'} y te avisamos en la campana. Si el cobro falla, se apaga y te avisamos también por correo.`,
        accion: p.auto_top_up.enabled ? 'Guardar' : 'Activar la recarga',
        ayudaTamano: precio === null ? null : `Cada recarga: ${precio}.`,
        // G1244: con un tamaño menor que el umbral, una recarga puede no bastar para superarlo.
        pequeno: tamano < umbral
            ? `Con ${mensajes(tamano)} y el umbral en ${miles(umbral)}, una recarga puede dejar el saldo aún por debajo del umbral y entonces no se vuelve a cobrar hasta que lo supere. Elige un tamaño mayor que el umbral.`
            : null,
        // G1241: activarla con el saldo ya bajo el umbral cobra con el siguiente mensaje gastado, no al momento.
        yaBajo: p.messages_available < umbral ? `Ahora quedan ${miles(p.messages_available)}, menos que el umbral: se cobrará con el siguiente mensaje que se gaste.` : null,
        hecho: {
            titulo: 'Recarga automática activada',
            texto: `Cuando queden menos de ${mensajes(umbral)} en el ${nombre.replace('Paquete', 'paquete')}, se comprarán ${mensajes(tamano)}${precio === null ? '' : ` por ${precio}`} y te avisaremos en la campana.`,
        },
    };
}

export const textosQuitar = (p: PaqueteWhatsApp) => {
    const nombre = NOMBRE_PAQUETE[p.pack];
    return {
        titulo: '¿Quitar la recarga automática?',
        texto: `${nombre} ya no se comprará solo. Cuando se acaben los mensajes, compra el paquete a mano en «Comprar más».`,
        hecho: { titulo: 'Recarga automática quitada', texto: `${nombre} ya no se recarga solo. Cuando se acaben los mensajes, compra el paquete a mano en «Comprar más».` },
    };
};

/** Valores iniciales de la ventana: lo guardado o, sin nada, 200 y el tamaño más pequeño que supera el umbral. */
export function borradorInicial(p: PaqueteWhatsApp): { umbral: number; tamano: number } {
    const umbral = p.auto_top_up.below_messages ?? 200;
    return { umbral, tamano: tamanoDe(p.auto_top_up.product_code) ?? TAMANOS.find((t) => t > umbral) ?? TAMANOS[0] };
}
